import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSupport } from "@/context/SupportContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LifebuoyIcon, PaperAirplaneIcon, XMarkIcon } from "@heroicons/react/24/outline";

const timeShort = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

// Floating "Chat with us" customer-care widget for logged-in customers (buyers/sellers/delivery).
export const SupportWidget = () => {
  const { user } = useAuth();
  const support = useSupport();
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [support?.messages, support?.open]);

  if (!user || !support || support.isStaff) return null; // staff answer from the admin Support inbox

  const { open, setOpen, openWidget, messages, send, customerUnread } = support;

  const submit = (e) => { e.preventDefault(); const t = draft.trim(); if (!t) return; send(t); setDraft(""); };

  return (
    <>
      {!open && (
        <button onClick={openWidget} data-testid="support-widget-button"
          className="fixed left-4 bottom-20 md:bottom-6 z-[70] flex items-center gap-2 rounded-full bg-primary text-white pl-3 pr-4 py-3 shadow-lg hover:brightness-110 transition-all">
          <LifebuoyIcon className="w-6 h-6" />
          <span className="text-sm font-semibold hidden sm:inline">Chat with us</span>
          {customerUnread > 0 && (
            <span data-testid="support-widget-unread" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">{customerUnread}</span>
          )}
        </button>
      )}

      {open && (
        <div data-testid="support-widget-panel" className="fixed left-4 bottom-20 md:bottom-6 z-[70] w-[92vw] max-w-sm rounded-2xl bg-white dark:bg-card shadow-2xl border border-border/60 flex flex-col overflow-hidden" style={{ height: "min(70vh, 520px)" }}>
          <div className="bg-primary text-white px-4 py-3 flex items-center gap-2">
            <LifebuoyIcon className="w-5 h-5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm leading-tight">Customer care</p>
              <p className="text-[11px] opacity-90">We usually reply within a few minutes</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="support-widget-close" aria-label="Close"><XMarkIcon className="w-5 h-5" /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-muted/20">
            {messages.length === 0 && (
              <div className="text-center text-xs text-muted-foreground py-8">
                <LifebuoyIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                Hi {user.name?.split(" ")[0] || "there"}! How can we help you today?
              </div>
            )}
            {messages.map((m) => {
              const mine = m.sender_role !== "support";
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-white rounded-br-sm" : "bg-white dark:bg-background border rounded-bl-sm"}`}>
                    {!mine && <p className="text-[10px] font-semibold text-primary mb-0.5">{m.sender_name || "Customer care"}</p>}
                    <p className="whitespace-pre-wrap break-words">{m.text}</p>
                    <p className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-muted-foreground"}`}>{timeShort(m.created_at)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <form onSubmit={submit} className="p-2.5 border-t border-border/60 flex items-center gap-2">
            <Input data-testid="support-message-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type your message…" className="rounded-full" />
            <Button type="submit" size="icon" className="rounded-full shrink-0" data-testid="support-message-send" aria-label="Send"><PaperAirplaneIcon className="w-5 h-5" /></Button>
          </form>
        </div>
      )}
    </>
  );
};
